import type { PairRiskSummaryDto } from '@rhcst/shared';

/**
 * The risk score display.
 *
 * Three of the six invariants in `docs/DESIGN_HANDOFF.md` live in this one small
 * component, because this is where the operator forms their impression of a pair
 * in about half a second. Each exists because rendering these states the obvious
 * way is actively misleading:
 *
 * **INVARIANT 1 — unscored is not a low score.** A score of 0 means a hard veto
 * fired: the most severe verdict available. "Not scored yet" is a completely
 * different statement. Rendered as a dash or an empty cell, the operator cannot
 * tell "the worst thing I have ever seen" from "I have no information about
 * this". So unscored is the literal word, in a dashed chip, with no numeral.
 *
 * **INVARIANT 2 — a partial assessment is not a clean one.** `complete: false`
 * means at least one rule could not run because its threshold is unconfigured.
 * Before this was surfaced, every pair scored 100/100 qualified — including a
 * pool holding two wei — because the rules that would have caught it silently
 * returned "not applicable" and everything that did run genuinely passed. It
 * looked exactly like success. So the numeral is wrapped in hazard stripes and
 * the count of skipped rules is stated beside it. It must be impossible to see a
 * high number without also seeing that it is partial.
 *
 * **INVARIANT 3 — a veto is not a score of 0.** A veto means untradeable
 * regardless of everything else; no amount of good liquidity or age can outweigh
 * it. Showing a numeral would invite reading it as a point on the same scale the
 * other pairs occupy. So the numeral is suppressed entirely and it renders as a
 * categorically different hazard block.
 *
 * The verdict, not the score, drives the colour. The bands are part of the
 * versioned ruleset, so a component that re-derived a colour from the number
 * could disagree with the stored verdict after a ruleset change.
 */

const VERDICT_LABEL: Record<PairRiskSummaryDto['verdict'], string> = {
  avoid: 'Avoid',
  risky: 'Risky',
  watch: 'Watch',
  qualified: 'Qualified',
};

export interface RiskBadgeProps {
  risk: PairRiskSummaryDto | null;
  /** How many rules were skipped for want of configuration, when known. */
  skippedCount?: number | undefined;
}

export function RiskBadge({ risk, skippedCount }: RiskBadgeProps) {
  // INVARIANT 1. No numeral, no dash, no empty cell.
  if (risk === null) {
    return (
      <span className="unscored" title="This pair has not been scored yet">
        Unscored
      </span>
    );
  }

  // INVARIANT 3. No numeral at all — a veto is not a point on the scale.
  if (risk.has_veto) {
    return (
      <span
        className="veto-block"
        title="A hard veto fired. This pair is untradeable regardless of its other rules — no combination of good liquidity or age can override it."
      >
        Veto
      </span>
    );
  }

  const scoreEl = (
    <span className="score" data-verdict={risk.verdict}>
      {risk.score}
    </span>
  );

  return (
    <span className="risk-cell">
      {/* INVARIANT 2. The hazard wrap makes the number itself look untrustworthy,
          which is the honest presentation of a partial reading. */}
      {risk.complete ? scoreEl : <span className="partial-wrap">{scoreEl}</span>}

      <span className="chip" data-verdict={risk.verdict}>
        {VERDICT_LABEL[risk.verdict]}
      </span>

      {!risk.complete && (
        <span
          className="partial-chip"
          title="Some risk checks did not run because their thresholds are unconfigured. This score reflects only the rules that could be applied — it is not a clean bill of health."
        >
          {skippedCount !== undefined && skippedCount > 0
            ? `Partial — ${skippedCount} skipped`
            : 'Partial'}
        </span>
      )}

      {risk.stale_ruleset && (
        <span
          className="chip"
          data-tone="muted"
          title={`Scored under ruleset ${risk.ruleset_version}, which is not the one now running. Rescore to compare it with the others.`}
        >
          Old rules
        </span>
      )}
    </span>
  );
}
